import React, { useState, useEffect } from 'react';
import { Zap, Sun, Battery, Droplets, Shield, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import HerosectionVideo from "../img/herosection.mp4";

const phrases = [
  'Empower the Minds',
  'Energize the Community',
  'Illuminate the Future'
];

const HeroSection = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timeout;

    if (!isDeleting) {
      if (displayText.length < currentPhrase.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentPhrase.slice(0, displayText.length + 1));
        }, 80);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 3000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 40);
      } else {
        setIsDeleting(false);
        setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentPhraseIndex]);

  const services = [
    { name: 'Electrical', icon: <Zap className="w-5 h-5" />, path: '/training/electrical', color: 'text-amber-400' },
    { name: 'Solar', icon: <Sun className="w-5 h-5" />, path: '/training/solar', color: 'text-amber-500' },
    { name: 'E-Mobility', icon: <Battery className="w-5 h-5" />, path: '/training/electric-vehicles', color: 'text-amber-400' },
    { name: 'Hydrogen', icon: <Droplets className="w-5 h-5" />, path: '/training/green-hydrogen', color: 'text-blue-400' },
    { name: 'OSHA', icon: <Shield className="w-5 h-5" />, path: '/training/safety-training', color: 'text-amber-600' },
    { name: 'Branding', icon: <Sparkles className="w-5 h-5" />, path: '/training/branding', color: 'text-amber-300' }
  ];

  return (
    <div className="bg-[#f4ebdc] min-h-screen font-sans">
      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#161616] text-white">

        {/* Background Video (shifted to the right logic using gradient mask) */}
        <div className="absolute inset-0 z-0 flex items-center justify-end">
          <div className="w-full md:w-[85%] lg:w-full h-full relative">
            <video
              src={HerosectionVideo}
              autoPlay
              loop
              muted
              playsInline
              onLoadedData={() => setVideoLoaded(true)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ${videoLoaded ? 'opacity-80' : 'opacity-0'}`}
            />
          </div>
          {/* Gradient overlay to make text readable on the left, fading out to the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#161616] via-[#161616]/50 to-transparent"></div>
        </div>

        {/* Main Content (Responsive Alignment) */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pt-48 md:pb-32 transition-all duration-1000 transform translate-y-0 opacity-100">
          <div className="max-w-4xl">

            {/* Headline Group */}
            <div className="mb-12 md:mb-16 mt-16 md:mt-24">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black tracking-tight leading-[1.1] min-h-[1.2em]">
                <span className="text-white inline">
                  {displayText}
                  <span className="inline-block w-[4px] h-[0.7em] bg-[#D97706] ml-2 animate-pulse align-middle"></span>
                </span>
              </h1>

              <p className="mt-6 md:mt-8 text-sm md:text-lg text-gray-300 font-medium max-w-xl leading-relaxed opacity-90">
                Advance your career with industry-leading training in emerging green technologies. We bridge the gap between technical expertise and the sustainable revolution.
              </p>
            </div>

            {/* CTA Buttons - Responsive Stack */}
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-start sm:items-center">
              <Link
                to="/training/upcoming"
                className="w-full sm:w-auto text-center group relative px-8 py-4 bg-[#D97706] text-white font-bold text-[10px] sm:text-xs uppercase tracking-widest rounded transition-all duration-300 hover:brightness-110 active:scale-95 flex items-center justify-center gap-2"
              >
                <span>Explore All Programs</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to="/training/about"
                className="w-full sm:w-auto text-center group relative px-8 py-4 bg-transparent border border-white/30 text-white font-bold text-[10px] sm:text-xs uppercase tracking-widest rounded transition-all duration-300 hover:bg-white/10 hover:border-white flex items-center justify-center gap-2"
              >
                <span>Learn More</span>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ── CORE PROGRAMS SECTION BELOW HERO ── */}
      <section className="py-20 md:py-28 bg-[#f4ebdc] relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-start gap-12 md:gap-16">

            {/* Left: Heading Panel */}
            <div className="md:w-2/5 md:sticky md:top-32">
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight mb-4 leading-tight">
                Core <br />
                <span className="text-[#D97706]">Programs</span>
              </h2>
              <p className="text-gray-600 font-medium text-base md:text-lg leading-relaxed">
                Equip yourself with industry-recognized certifications and practical skills to lead in the sustainable technology revolution.
              </p>
              <Link to="/training/upcoming" className="hidden md:inline-flex mt-8 items-center gap-2 text-[#D97706] font-bold text-sm uppercase tracking-wider hover:text-[#B45309] transition-colors group">
                View All Programs
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Right: Program Grid */}
            <div className="md:w-3/5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {services.map((service, index) => (
                  <Link
                    to={service.path}
                    key={service.name}
                    className="group relative flex flex-col items-center justify-center p-5 md:p-7 bg-white/60 backdrop-blur-xl hover:bg-white transition-all duration-700 rounded-[2rem] border border-white/80 hover:border-amber-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(217,119,6,0.15)] cursor-pointer transform hover:-translate-y-3 overflow-hidden"
                  >
                    <div className="relative z-10 w-14 h-14 md:w-16 md:h-16 mb-3 md:mb-4 rounded-2xl bg-white flex items-center justify-center text-[#D97706] group-hover:bg-[#D97706] group-hover:text-white group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-sm border border-gray-100">
                      {service.icon}
                    </div>

                    <span className="relative z-10 text-[11px] md:text-[12px] font-black uppercase tracking-[0.15em] text-gray-800 group-hover:text-[#D97706] text-center transition-colors duration-500">
                      {service.name}
                    </span>

                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1.5 bg-[#D97706] rounded-t-full transition-all duration-500 group-hover:w-10 md:group-hover:w-12"></div>
                  </Link>
                ))}
              </div>

              <Link to="/training/upcoming" className="md:hidden mt-8 flex items-center justify-center gap-2 text-[#D97706] font-bold text-sm uppercase tracking-wider hover:text-[#B45309] transition-colors group">
                View All Programs
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default HeroSection;